import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, logAdminActivity } from '../../lib/firebase';
import { Flag, Check, X, Trash2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

interface Report {
  id: string;
  treeId: string;
  treeName: string;
  reason: string;
  status: 'pending' | 'resolved' | 'dismissed';
  createdAt: Date;
}

export default function AdminReports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'resolved' | 'dismissed'>('all');

  const loadReports = async () => {
    try {
      const reportsQuery = query(
        collection(db, 'reports'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(reportsQuery);
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Report[];
      setReports(reportsData);
    } catch (error) {
      console.error('Error loading reports:', error);
      toast.error('Error al cargar los reportes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, []);

  const handleUpdateStatus = async (reportId: string, newStatus: 'resolved' | 'dismissed') => {
    try {
      const reportRef = doc(db, 'reports', reportId);
      await updateDoc(reportRef, { status: newStatus });
      
      await logAdminActivity('update_report', {
        reportId,
        newStatus,
        timestamp: new Date()
      });

      setReports(reports.map(report => 
        report.id === reportId ? { ...report, status: newStatus } : report
      ));

      toast.success(`Reporte ${newStatus === 'resolved' ? 'resuelto' : 'descartado'}`);
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Error al actualizar el reporte');
    }
  };

  const handleDelete = async (reportId: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este reporte?')) return;

    try {
      await deleteDoc(doc(db, 'reports', reportId));
      
      await logAdminActivity('delete_report', {
        reportId,
        timestamp: new Date()
      });

      setReports(reports.filter(report => report.id !== reportId));
      toast.success('Reporte eliminado');
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error('Error al eliminar el reporte');
    }
  };

  const filteredReports = reports.filter(report => 
    filter === 'all' ? true : report.status === filter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <Flag className="h-6 w-6 mr-2 text-primary" />
          Reportes
        </h2>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">Todos</option>
          <option value="pending">Pendientes</option>
          <option value="resolved">Resueltos</option>
          <option value="dismissed">Descartados</option>
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="relative">
          {/* Header - Fixed */}
          <div className="sticky top-0 z-10">
            <div className="bg-gray-50 border-b border-gray-200">
              <div className="min-w-[1000px]">
                <div className="grid grid-cols-[2fr_3fr_200px_120px_120px] gap-4 px-6 py-3">
                  <div className="text-xs font-medium text-gray-500 uppercase">Árbol</div>
                  <div className="text-xs font-medium text-gray-500 uppercase">Motivo</div>
                  <div className="text-xs font-medium text-gray-500 uppercase">Fecha</div>
                  <div className="text-xs font-medium text-gray-500 uppercase">Estado</div>
                  <div className="text-xs font-medium text-gray-500 uppercase">Acciones</div>
                </div>
              </div>
            </div>
          </div>

          {/* Table Body - Scrollable */}
          <div className="overflow-x-auto">
            <div className="min-w-[1000px]">
              {filteredReports.map((report) => (
                <div 
                  key={report.id}
                  className="grid grid-cols-[2fr_3fr_200px_120px_120px] gap-4 px-6 py-4 hover:bg-gray-50 items-center border-b border-gray-200"
                >
                  <div>
                    <Link
                      to={`/arbol/${report.treeId}`}
                      className="text-primary hover:text-primary-dark flex items-center"
                      target="_blank"
                    >
                      {report.treeName}
                      <ExternalLink className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {report.reason}
                    </p>
                  </div>
                  <div className="text-sm text-gray-600">
                    {report.createdAt.toLocaleString()}
                  </div>
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      report.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      report.status === 'resolved' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {report.status === 'pending' ? 'Pendiente' :
                       report.status === 'resolved' ? 'Resuelto' :
                       'Descartado'}
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    {report.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleUpdateStatus(report.id, 'resolved')}
                          className="p-1 text-green-500 hover:bg-green-50 rounded"
                          title="Marcar como resuelto"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(report.id, 'dismissed')}
                          className="p-1 text-gray-500 hover:bg-gray-50 rounded"
                          title="Descartar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(report.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}

              {filteredReports.length === 0 && (
                <div className="text-center py-12">
                  <Flag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No hay reportes {filter !== 'all' ? `${filter}s` : ''}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}